import { TimeInput } from '@components/timeInput';
import { FormField } from '@components/ui/form';
import { DatePicker } from '@components/ui/form/date';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { useTranslation } from 'react-i18next';
import { getHours, getMinutes, parseISO, set } from 'date-fns';
import { InternalScheduleDateInputsProps } from './types';
import { useFormContext } from 'react-hook-form';
import { useRescheduler } from '@contexts/rescheduler';

export const InternalScheduleDateInputs = ({
  data,
  disabled,
  children,
}: InternalScheduleDateInputsProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'forms' });
  const rescheduler = useRescheduler();

  const { control, setValue, watch } = useFormContext<{
    startsAt?: string;
    endsAt?: string;
  }>();

  const { startsAt, endsAt } = watch();

  const parseValue = (value?: unknown) => {
    return parseISO((value || '') as string);
  };

  const setStartAndEndDateValue = (newDateValue: Date) => {
    const newDateString = newDateValue.toISOString();
    const initialStartsAt = parseISO(startsAt || '');
    const updatedStartsAt = set(newDateString, {
      hours: getHours(initialStartsAt),
      minutes: getMinutes(initialStartsAt),
    });

    const initialEndsAt = parseISO(endsAt || '');
    const updatedEndsAt = set(newDateString, {
      hours: getHours(initialEndsAt),
      minutes: getMinutes(initialEndsAt),
    });

    setValue('startsAt', updatedStartsAt.toISOString());
    setValue('endsAt', updatedEndsAt.toISOString());
  };

  return (
    <div className="flex gap-4">
      <FormField
        control={control}
        name="startsAt"
        render={({ field: { value, onChange, ...field } }) => (
          <FormItemWrapper className="w-fit" label={t('date.label')} required>
            <DatePicker
              value={parseValue(value)}
              onChange={(v) => {
                if (data && rescheduler) rescheduler.removeSavedSchedule(data);
                if (!v || !startsAt || !endsAt) return;
                setStartAndEndDateValue(v);
              }}
              mode="single"
              className="w-[180px]"
              disabled={disabled}
              placeholder={t('default.select.placeholder')}
              noRemove
              {...field}
            />
          </FormItemWrapper>
        )}
      />
      <FormField
        control={control}
        name="startsAt"
        render={({ field: { value, onChange, ...field } }) => (
          <FormItemWrapper className="w-fit" label={t('startTime.label')} required>
            <TimeInput
              {...field}
              asInput
              internal
              id={field.name}
              value={parseValue(value)}
              disabled={disabled}
              onValueChange={(v) => {
                if (data && rescheduler) rescheduler.removeSavedSchedule(data);
                if (!v || !startsAt) return;
                setValue(
                  'startsAt',
                  set(parseISO(startsAt), {
                    hours: getHours(v),
                    minutes: getMinutes(v),
                  }).toISOString(),
                );
              }}
            />
          </FormItemWrapper>
        )}
      />
      <FormField
        control={control}
        name="endsAt"
        render={({ field: { value, onChange, ...field } }) => (
          <FormItemWrapper className="w-fit" label={t('endTime.label')} required>
            <TimeInput
              {...field}
              asInput
              internal
              id={field.name}
              value={parseValue(value)}
              disabled={disabled}
              onValueChange={(v) => {
                if (data && rescheduler) rescheduler.removeSavedSchedule(data);
                if (!v || !endsAt) return;
                setValue(
                  'endsAt',
                  set(parseISO(endsAt), {
                    hours: getHours(v),
                    minutes: getMinutes(v),
                  }).toISOString(),
                );
              }}
            />
          </FormItemWrapper>
        )}
      />
      {children}
    </div>
  );
};
